import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import moment from 'moment';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';
import LandmanBillException from 'src/models/personnel/LandmanBillException';
import LandmanBillExceptionHistory from 'src/models/personnel/LandmanBillExceptionHistory';

interface IProjectBillExceptionAndHistoryDialogProps {
    dialogName: string;
    billExceptions: Array<LandmanBillException>;
    hideDialog: () => void;
    selectedPersonnel: TSDILandman;
}

const ProjectBillExceptionAndHistoryDialog = (props: IProjectBillExceptionAndHistoryDialogProps) => {
    const { get, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [projectBillExceptions, setProjectBillExceptions] = useState<Array<LandmanBillExceptionHistory>>();
    const [projectName, setProjectName] = useState<string>();
    const [selectedId, setSelectedId] = useState<number>();

    useEffect(() => {
        onCreate();
    }, []);

    const onCreate = () => {
        console.log('PayExceptionHistoryDialog onCreate()', props.billExceptions);
    };

    const OKClicked = () => {
        props.hideDialog();
    };

    // when current exception is clicked
    const getPayHistoryByProject = async (projectId: number) => {
        if (props?.selectedPersonnel?.tsdNo !== undefined && props?.selectedPersonnel?.tsdNo !== null && props?.selectedPersonnel?.tsdNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${props?.selectedPersonnel?.tsdNo}/projects/${projectId}/get-project-bill-exceptions`);

            if (response.ok) {
                setProjectBillExceptions(data);
            } else {
                showToast('Unable to get selected personnel\'s pay history information for this project', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided: ' + props?.selectedPersonnel?.tsdNo, `rgba(255,0,0,0.5)`);
        }
    };

    const getPayByValue = (value: string) => {
        switch (value) {
            case 'D':
                return 'Day';
            case 'H':
                return 'Hour';
            case 'M':
                return 'Month';
            default:
                return 'Unknown';
        }
    };

    const setSelectedRow = async (rowInfo: LandmanBillException) => {
        setSelectedId(rowInfo.prId);
        setProjectName(rowInfo.projectName);
        getPayHistoryByProject(rowInfo.prId);
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    return (
        <Dialog title="Project Bill Exception History" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ maxHeight: '75%', width: '65%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <h3>Bill Exceptions</h3>
            {props.billExceptions?.length > 0 && <div style={{ overflowY: 'scroll', maxHeight: '35rem' }}>
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr style={{ background: '#d6d6d6' }}>
                            <th style={{ width: '5%' }}>Client</th>
                            <th style={{ width: '20%' }}>Client Name</th>
                            <th style={{ width: '5%' }}>Project</th>
                            <th style={{ width: '20%' }}>Project Name</th>
                            <th style={{ width: '10%' }}>Pay Rate</th>
                            <th style={{ width: '5%' }}>Pay Unit</th>
                            <th style={{ width: '10%' }}>Classification</th>
                        </tr>
                    </thead>
                    {props.billExceptions?.map((history: LandmanBillException, index: number) => {
                        return (
                            <tbody key={`body-${index}`}>
                                <tr 
                                    key={`p-${index}`} 
                                    onClick={() => setSelectedRow(history)}
                                    style={{ width: '100%', backgroundColor: selectedId === history?.prId ? 'yellow' : undefined }}
                                >
                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.clNo}
                                    </td>
                                    <td style={{ width: '20%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.clientName}
                                    </td>
                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.prNo}
                                    </td>
                                    <td style={{ width: '20%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {history?.projectName}
                                    </td>
                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {parseFloat(history?.billRate?.toString()).toFixed(2)}
                                    </td>
                                    <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        {getPayByValue(history?.billUnit)}
                                    </td>
                                    <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                        TBD
                                    </td>
                                </tr>
                            </tbody>
                        );
                    })}
                </table>
            </div>}
            <br />
            <hr />
            <h3>Bill Exceptions History Detail {projectName ? `for ${projectName}` : ''}</h3>
            {projectBillExceptions?.length > 0 && <div>
                <div style={{ overflowY: 'scroll', maxHeight: '25rem' }}>
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#d6d6d6' }}>
                                <th style={{ width: '10%' }}>Pay By</th>
                                <th style={{ width: '10%' }}>Pay Rate</th>
                                <th style={{ width: '15%' }}>Eff Date</th>
                                <th style={{ width: '10%' }}>Manager</th>
                                <th style={{ width: '25%' }}>Manager Name</th>
                                <th style={{ width: '10%' }}>Prod. Bonus</th>
                                <th style={{ width: '10%' }}>Prev Pay By</th>
                                <th style={{ width: '10%' }}>Prev Pay Rate</th>
                            </tr>
                        </thead>
                        {projectBillExceptions?.map((pay: LandmanBillExceptionHistory, index: number) => {
                            return (
                                <tbody key={index}>
                                    <tr className={`contract-pay-history-active-item-${index}`}>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {getPayByValue(pay.billUnit)}
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {parseFloat(pay.billRate?.toString()).toFixed(2)}
                                        </td>
                                        <td style={{ width: '15%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {moment(pay.chgDate).format('YYYY-MM-DD') as unknown as Date}
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            TBD
                                        </td>
                                        <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {pay.tsdUser}
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            ?
                                        </td>
                                        <td style={{ width: '10%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {getPayByValue(pay.prevUnit)}
                                        </td>
                                        <td style={{ width: '15%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {parseFloat(pay.prevRate?.toString()).toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            );
                        })}
                    </table>
                </div>
            </div>}
            {props.billExceptions?.length <= 0 && <div className="bold-centered">No Bill Exceptions for Project</div>}
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center', textAlign: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Close'}
                    onClick={() => OKClicked()}
                />
            </div>
        </Dialog>
    );
};

export default ProjectBillExceptionAndHistoryDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/pay/ProjectBillExceptionAndHistoryDialog.tsx